import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sprout, ClipboardCheck, Heart, MessageCircle, Apple, Users, LineChart, BookOpen } from 'lucide-react';
import { Logo } from '../../Logo';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function Home() {
  return <div className="w-full" data-id="element-1754">
      {/* SPLIT HERO */}
      <section className="relative min-h-screen flex items-center pt-20 bg-cover bg-center" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/7Nio93yQvpecyCZYaLP2wa/file_00000000403871f89bf6d32bcc72887f.png')`
    }} data-id="element-1755">
        {/* Cream gradient overlay for text readability on left */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#F5F5F0]/95 via-[#F5F5F0]/70 to-transparent pointer-events-none" data-id="element-1756" />

        <div className="relative w-full max-w-7xl mx-auto px-6" data-id="element-1757">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1758">
            {/* Left: Text Content */}
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="flex flex-col" data-id="element-1759">
              <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-tealslate uppercase tracking-[0.25em] mb-6" data-id="element-1760">
                Jack Achenbach · MS, RDN, CPT
              </motion.p>

              <motion.h1 variants={fadeUpVariants} className="text-5xl md:text-6xl lg:text-7xl font-serif text-[#1F1F1F] leading-[1.1] mb-2" data-id="element-1761">
                Structured nutrition.
              </motion.h1>

              <motion.h1 variants={fadeUpVariants} className="text-5xl md:text-6xl lg:text-7xl font-serif text-tealslate italic leading-[1.1] mb-8" data-id="element-1762">
                Real results.
              </motion.h1>

              <motion.p variants={fadeUpVariants} className="text-lg md:text-xl text-[#3B3B3B] max-w-xl mb-10 leading-relaxed" data-id="element-1763">
                Evidence-based nutrition coaching for GLP-1 support, body
                composition, and sustainable metabolic health.
              </motion.p>

              <motion.div variants={fadeUpVariants} className="flex flex-col sm:flex-row items-start gap-4" data-id="element-1764">
                <Link to="/contact" className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-[#4A5A5F] transition-colors" data-id="element-1765">
                  Book a Consultation
                </Link>
                <Link to="/services" className="inline-flex items-center gap-3 text-sm font-bold text-[#1F1F1F] hover:text-tealslate transition-colors group" data-id="element-1766">
                  <div className="w-10 h-10 rounded-full border-2 border-[#1F1F1F] group-hover:border-tealslate flex items-center justify-center transition-colors" data-id="element-1767">
                    <Play className="w-4 h-4 fill-current" data-id="element-1768" />
                  </div>
                  Learn More
                </Link>
              </motion.div>
            </motion.div>

            {/* Right side intentionally empty — background image shows through */}
            <div className="hidden lg:block" data-id="element-1769" />
          </div>
        </div>
      </section>

      {/* VALUE PROPS STRIP */}
      <section className="bg-[#FAFAF8] border-t border-[#E5E5E0] py-16 px-6" data-id="element-1770">
        <div className="max-w-7xl mx-auto" data-id="element-1771">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" data-id="element-1772">
            {[{
            icon: Sprout,
            title: 'Personalized Plans',
            desc: 'Nutrition tailored to your goals and medication.'
          }, {
            icon: ClipboardCheck,
            title: 'Evidence-Based',
            desc: 'Built on peer-reviewed research, not trends.'
          }, {
            icon: Heart,
            title: 'Sustainable Habits',
            desc: 'Strategies that outlast the prescription.'
          }, {
            icon: MessageCircle,
            title: 'Ongoing Support',
            desc: 'Direct access to a coach who answers.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="flex flex-col items-center text-center" data-id="element-1773">
                <item.icon className="w-7 h-7 text-tealslate mb-4" data-id="element-1774" />
                <h3 className="text-base font-serif text-[#1F1F1F] mb-2" data-id="element-1775">
                  {item.title}
                </h3>
                <p className="text-sm text-[#5A5A5A]" data-id="element-1776">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ABOUT ME BLOCK */}
      <section className="bg-[#F5F5F0] py-24 md:py-32 px-6" data-id="element-1777">
        <div className="max-w-7xl mx-auto" data-id="element-1778">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1779">
            {/* Left: Food Photo */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-1780">
              <img src="https://cdn.magicpatterns.com/uploads/fBWp7VcHydeQL4QeV4Xzp2/pasted-image.png" alt="Jack Achenbach" className="w-full h-[500px] object-cover object-top shadow-xl" data-id="element-1781" />
            </motion.div>

            {/* Right: Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1782">
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1783">
                About Me
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-3" data-id="element-1784">
                Hi, I'm Jack.
              </h2>
              <p className="text-base text-[#5A5A5A] mb-6" data-id="element-1785">
                Registered Dietitian Nutritionist · Certified Personal Trainer
              </p>
              <div className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-1786" />
              <p className="text-base md:text-lg text-[#3B3B3B] leading-relaxed mb-8" data-id="element-1787">
                I help people on GLP-1 medications protect their muscle,
                optimize their nutrition, and build habits that last beyond the
                prescription. My approach combines clinical evidence with
                real-world coaching — because structure is what turns a
                medication into a transformation.
              </p>
              <Link to="/services" className="inline-block px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-[#4A5A5F] transition-colors" data-id="element-1788">
                More About Me
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW I CAN HELP */}
      <section className="bg-[#EAEAE3] py-8 md:py-12 px-6" data-id="element-1789">
        <div className="max-w-7xl mx-auto" data-id="element-1790">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-6" data-id="element-1791">
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-3" data-id="element-1792">
              How I Can Help
            </h2>
            <div className="w-16 h-0.5 bg-tealslate mx-auto" data-id="element-1793" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1794">
            {[{
            icon: Apple,
            title: '1:1 GLP-1 Coaching',
            desc: 'Personalized nutrition guidance to maximize results and minimize side effects.'
          }, {
            icon: Users,
            title: 'Body Composition',
            desc: 'Protein and training protocols to preserve lean mass during weight loss.'
          }, {
            icon: LineChart,
            title: 'Off-Medication Strategy',
            desc: 'Exit plans and habit systems to maintain results after stepping down.'
          }, {
            icon: BookOpen,
            title: 'Education & Resources',
            desc: 'Guides, supplements, and tools that simplify metabolic health.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="bg-white p-5 flex flex-col items-center text-center border border-[#E5E5E0] hover:shadow-lg transition-shadow" data-id="element-1795">
                <div className="w-12 h-12 rounded-full bg-tealslate/10 flex items-center justify-center mb-4" data-id="element-1796">
                  <item.icon className="w-6 h-6 text-tealslate" data-id="element-1797" />
                </div>
                <h3 className="text-xl font-serif text-[#1F1F1F] mb-3" data-id="element-1798">
                  {item.title}
                </h3>
                <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1799">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* TRAINING APP TEASER - image background with overlaid text */}
      <section className="relative px-6 border-t border-darkgray overflow-hidden bg-cover bg-center" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/btHwB7Y6BD4NPyCHt86Hf7/app.png')"
    }} data-id="element-1800">
        {/* Dark gradient overlay for text legibility on the left */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" data-id="element-1801" />

        <div className="relative z-10 max-w-7xl mx-auto py-16 md:py-24" data-id="element-1802">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} className="max-w-xl" data-id="element-1803">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-2" data-id="element-1804">
              Structured Training App
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-4" data-id="element-1805">
              Programmed workouts. In your pocket.
            </h2>
            <div className="w-10 h-0.5 bg-tealslate mb-3" data-id="element-1806" />
            <p className="text-sm md:text-base text-[#D8D8D0] leading-relaxed mb-3 max-w-xl" data-id="element-1807">
              Coach-built training designed to preserve muscle, build strength,
              and maximize your GLP-1 results. Video demos, progress tracking,
              and direct messaging — all for less than a coffee a week.
            </p>

            <div className="flex items-baseline gap-2 mb-5" data-id="element-1808">
              <span className="text-4xl font-serif text-white" data-id="element-1809">$6.99</span>
              <span className="text-xs text-[#9CA3AF] uppercase tracking-widest" data-id="element-1810">
                / month
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3" data-id="element-1811">
              <Link to="/training" className="px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-lightteal transition-colors text-center inline-flex items-center justify-center gap-2" data-id="element-1812">
                Explore Training <ArrowRight className="w-4 h-4" data-id="element-1813" />
              </Link>
              <a href="https://www.trainerize.me/profile/structured/?planGUID=0adc7cc9d3274ca183f4a7ca2a0e16a3" target="_blank" rel="noopener noreferrer" className="px-6 py-3 border border-[#D8D8D0] text-[#D8D8D0] text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors text-center" data-id="element-1814">
                Sign Up Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA BANNER - full-width image with text overlaid */}
      <section className="relative w-full overflow-hidden" data-id="element-1815">
        <div className="relative w-full bg-cover bg-center min-h-[320px] md:min-h-[380px]" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1800&q=80')"
      }} data-id="element-1816">
          {/* Left gradient overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F5F5F0] via-[#F5F5F0]/85 to-transparent" data-id="element-1817" />

          <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 flex items-center min-h-[320px] md:min-h-[380px]" data-id="element-1818">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="max-w-xl" data-id="element-1819">
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-5 leading-tight" data-id="element-1820">
                Ready to take the next step?
              </h2>
              <p className="text-base md:text-lg text-[#3B3B3B] mb-8 leading-relaxed" data-id="element-1821">
                Let's build a nutrition plan that works for your body and your
                life.
              </p>
              <Link to="/contact" className="inline-block px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-[#4A5A5F] transition-colors" data-id="element-1822">
                Book a Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}